import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_BODY = 180;

export default function TestimonialPage() {
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const trimmedBody = body.trim();
  const canSubmit = name.trim().length >= 1 && trimmedBody.length >= 10 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/testimonials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), city: city.trim(), body: trimmedBody }),
      });
      if (!res.ok) throw new Error("submit failed");
      setDone(true);
      toast.success("Thank you", { description: "We read every one. If it fits the marquee, it'll rotate in soon." });
    } catch {
      toast.error("Couldn't send your whisper — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10">
      <header className="sticky top-0 z-30 backdrop-blur-md border-b border-[color:var(--gold)]/12" style={{ background: "rgba(5,9,23,0.72)" }}>
        <div className="mx-auto max-w-[900px] px-6 md:px-10 py-4 md:py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-90" data-testid="testimonial-back">
            <ArrowLeft size={14} className="text-[color:var(--gold)]" />
            <span className="label-overline">Back to Earth Pulse</span>
          </Link>
          <Sparkles size={14} className="text-[color:var(--gold)]" />
        </div>
      </header>

      <main className="mx-auto max-w-[720px] px-6 md:px-10 py-16 md:py-24" data-testid="testimonial-page">
        <div className="label-overline">Leave a whisper</div>
        <h1 className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.05] tracking-tight">
          Say one <span className="font-serif-italic text-[color:var(--gold)]">honest</span> thing.
        </h1>
        <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg leading-relaxed">
          If Earth Pulse has helped you in some small way — a calmer morning, a decoded headache,
          a birth chart that landed — leave a line here. Approved whispers rotate through the homepage
          marquee so the next sensitive soul who lands knows they&apos;re not alone.
        </p>

        {done ? (
          <div
            className="mt-12 rounded-2xl border border-[color:var(--gold)]/40 p-8 text-center"
            style={{ background: "linear-gradient(135deg, rgba(212,162,76,0.08), rgba(229,168,124,0.03))" }}
            data-testid="testimonial-done"
          >
            <Sparkles size={22} className="text-[color:var(--gold)] mx-auto" />
            <h2 className="mt-4 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl">
              Received with gratitude.
            </h2>
            <p className="mt-4 font-serif-italic text-[color:var(--parchment-soft)]">
              We read every one by hand. If it fits the marquee, it&apos;ll rotate in within a day or two.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="testimonial-return-home"
            >
              Back to your guidance
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 space-y-6" data-testid="testimonial-form">
            <div>
              <div className="label-overline mb-2">Your name</div>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name is fine"
                required
                maxLength={40}
                className="bg-white border-slate-300 rounded-lg h-12 text-slate-900 font-sans-body"
                data-testid="testimonial-name"
              />
            </div>

            <div>
              <div className="label-overline mb-2">Where in the world (optional)</div>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Brighton, Toronto, Lisbon…"
                maxLength={40}
                className="bg-white border-slate-300 rounded-lg h-12 text-slate-900 font-sans-body"
                data-testid="testimonial-city"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-overline">Your whisper</div>
                <span className="font-serif-italic text-[color:var(--text-muted)] text-xs">
                  {trimmedBody.length}/{MAX_BODY}
                </span>
              </div>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
                placeholder="One honest line about how Earth Pulse landed for you…"
                required
                minLength={10}
                maxLength={MAX_BODY}
                className="bg-white border-slate-300 rounded-lg text-slate-900 font-sans-body min-h-[120px]"
                data-testid="testimonial-body"
              />
              <p className="mt-2 font-serif-italic text-[color:var(--text-muted)] text-xs">
                Please keep it real — one sentence is enough. We approve by hand before publishing.
              </p>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full h-12 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium disabled:opacity-40"
                data-testid="testimonial-submit"
              >
                {submitting ? "Sending…" : "Send your whisper"}
              </Button>
              <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs text-center">
                No account, no email, no data harvest. Just your line.
              </p>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
